import { GraphQlPriceType } from '@models/graphql';
import type { TFunction } from 'next-i18next';
import { COUNTRIES_INCLUDED_COPPER_PRICE_IN_PRICE_PER_METER } from './constants';
import { getUnitLabelForUnitSystem, LengthCode, UnitSystem } from '@icommerce/units';
import { KetteMath } from '@igus/echain-math';
import { convertProductAttributeValueAmountUnit } from '@libs/units';
import type { Category } from '@models/data';
import { showCuttingCostInformationInBuyBox } from '@libs/cutting-cost.helper';
import type Locale from '@formatjs/intl-locale';

export const chainEffectiveLengthCalculator =
  (division: number): ((numLinks: number) => number) =>
  numLinks =>
    KetteMath.calcEffektiveLaenge_mm(numLinks, division);

export const chainNumLinksCalculator =
  (division: number): ((length: number) => number) =>
  length =>
    KetteMath.calcNumGlieder_1(length, division);

export interface BuyBoxConfiguration {
  discountUnitLabel: string;
  defaultLength: number; // how many units should be selected by default
  showLengthInput: boolean;
  showPieceInput: boolean;
  showQuantityInput: boolean;
  showNumChainLinksInput: boolean;
  defaultNumChainLinks: number;
  showCuttingCostInfo: boolean;
  showCopperSurchargeInfo: boolean;
  showDiscountPrice: boolean;
}

export const DEFAULT_LENGTH_FOR_PRICE_TYPE: Record<GraphQlPriceType, number> = {
  [GraphQlPriceType.LengthAndPieceWithoutCopper]: 100,
  [GraphQlPriceType.LengthAndPiece]: 1,
  [GraphQlPriceType.LengthWithoutCopper]: 1,
  [GraphQlPriceType.LengthWithCuttingCostWithoutCopper]: 1000,
  [GraphQlPriceType.TribotapeLength]: 1000,
  [GraphQlPriceType.Piece]: -1,
  [GraphQlPriceType.PieceWithChainLengthDependency]: -1,
  [GraphQlPriceType.PieceWithCopper]: -1,
  [GraphQlPriceType.Length]: 1,
  [GraphQlPriceType.LeadScrewLength]: 1000,
  [GraphQlPriceType.ChainLink]: 1000, // mm
  [GraphQlPriceType.ChainLength]: 1000, // mm
};

export const chainDefaultValues = (
  teilung: number,
  unitSystem: UnitSystem,
  priceType: GraphQlPriceType
): { numChainLinks: number; effectiveLength: number } => {
  const lengthUnit = unitSystem === UnitSystem.metric ? LengthCode.MILLIMETER : LengthCode.FEET;
  const numGlieder = KetteMath.calcNumGlieder_1(
    convertProductAttributeValueAmountUnit({ Value: DEFAULT_LENGTH_FOR_PRICE_TYPE[priceType], Unit: 'mm' }, lengthUnit),
    teilung
  );
  return {
    numChainLinks: numGlieder,
    effectiveLength: KetteMath.calcEffektiveLaenge_mm(numGlieder, teilung),
  };
};

export const getBuyBoxConfiguration = (
  priceType: GraphQlPriceType,
  unitSystem: UnitSystem,
  t: TFunction,
  locale: Locale,
  priceCountry: string,
  categoryTree: Category,
  chainLinkDivision?: number
): BuyBoxConfiguration | undefined => {
  const chainValues =
    (priceType === GraphQlPriceType.ChainLink || priceType === GraphQlPriceType.ChainLength) && chainLinkDivision
      ? chainDefaultValues(chainLinkDivision, unitSystem, priceType)
      : { numChainLinks: 0, effectiveLength: 0 };

  switch (priceType) {
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
      return {
        discountUnitLabel: t('PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION'),
        defaultLength: 100,
        showLengthInput: true,
        showPieceInput: false,
        showQuantityInput: true,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, false),
        showCopperSurchargeInfo: false,
        showDiscountPrice: false,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.LengthAndPiece:
      return {
        discountUnitLabel: t('PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION'),
        defaultLength: 1,
        showLengthInput: true,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, true),
        showCopperSurchargeInfo: true,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.LengthWithoutCopper:
      return {
        discountUnitLabel: getUnitLabelForUnitSystem(LengthCode.METER, unitSystem),
        defaultLength: 1,
        showLengthInput: true,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, true),
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
      return {
        discountUnitLabel: getUnitLabelForUnitSystem(LengthCode.METER, unitSystem),
        defaultLength: 1000,
        showLengthInput: true,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, true),
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.TribotapeLength:
      return {
        discountUnitLabel: getUnitLabelForUnitSystem(LengthCode.METER, unitSystem),
        defaultLength: 1000,
        showLengthInput: true,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, true),
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.Piece:
    case GraphQlPriceType.PieceWithChainLengthDependency:
      return {
        discountUnitLabel: t('PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION'),
        defaultLength: -1,
        showLengthInput: false,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, false),
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.PieceWithCopper:
      return {
        discountUnitLabel: t('PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION'),
        defaultLength: -1,
        showLengthInput: false,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, false),
        showCopperSurchargeInfo: true,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.Length:
      return {
        discountUnitLabel: getUnitLabelForUnitSystem(LengthCode.METER, unitSystem),
        defaultLength: 1,
        showLengthInput: true,
        showPieceInput: true,
        showQuantityInput: false,
        showCuttingCostInfo: showCuttingCostInformationInBuyBox(locale, priceCountry, categoryTree, true),
        showCopperSurchargeInfo: true,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.LeadScrewLength:
      return {
        discountUnitLabel: t('PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION'),
        defaultLength: 1000,
        showLengthInput: true,
        showPieceInput: false,
        showQuantityInput: true,
        showCuttingCostInfo: false,
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: false,
        defaultNumChainLinks: 0,
      };
    case GraphQlPriceType.ChainLink:
      return {
        discountUnitLabel: t('PRODUCT_OVERVIEW.PRICE_BOX.CHAINLINK.UNIT_SINGULAR'),
        defaultLength: chainValues.effectiveLength,
        showLengthInput: true,
        showPieceInput: false,
        showQuantityInput: true,
        showCuttingCostInfo: false,
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: true,
        defaultNumChainLinks: chainValues.numChainLinks,
      };
    case GraphQlPriceType.ChainLength:
      return {
        discountUnitLabel: getUnitLabelForUnitSystem(LengthCode.METER, unitSystem),
        defaultLength: chainValues.effectiveLength,
        showLengthInput: true,
        showPieceInput: false,
        showQuantityInput: true,
        showCuttingCostInfo: false,
        showCopperSurchargeInfo: false,
        showDiscountPrice: true,
        showNumChainLinksInput: true,
        defaultNumChainLinks: chainValues.numChainLinks,
      };
  }
};

/**
 * In some countries the  copper calculation information and links should not be shown because copper price is already added to price per meter.
 *
 * @param originalValue The original value which is set by getBuyBoxConfiguration
 * @param priceCountry used to decide whether or not to show copper surcharge info
 * @param isSessionAvailable We check for the session to ensure that we do not use the DEFAULT_PRICE_COUNTRY
 * @returns boolean
 */
export const showCopperSurchargeInfoBasedOnPriceCountry = (
  originalValue: boolean,
  priceCountry: string,
  isSessionAvailable: boolean
): boolean => {
  // We ensure here not to show the copper information until the session information has been received
  if (!isSessionAvailable) {
    return false;
  }
  if (COUNTRIES_INCLUDED_COPPER_PRICE_IN_PRICE_PER_METER.includes(priceCountry)) {
    return false;
  }
  return originalValue;
};

export const getCuttingCostTranslationKeyByPriceType = (priceType: GraphQlPriceType): string => {
  switch (priceType) {
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.TribotapeLength:
      return 'BUY_BOX.PRICE_PLUS_GENERAL_CUTTING_COSTS_INFO_SHORTCUT';
    default:
      return 'BUY_BOX.PRICE_PLUS_CUTTING_COSTS_INFO_SHORTCUT';
  }
};
